import { useState, useEffect, useCallback, useRef } from 'react';
import { renderMarkdown } from '../lib/markdown';
import { apiClient } from '../lib/api-client';
import { validateCefrDoc, patternsReady, type ValidationResult, type CheckResult } from '../lib/cefr-validator';
import { generateEmails, uploadEmails, type GenerationLog, type GeneratedEmail } from '../lib/email-generator';
import { langName } from '../lib/lang-names';

// ── Types ──────────────────────────────────────────────────────────

interface EmailOutline {
  week: number;
  level: string;
  subject: string;
  topic: string;
  cefr_source: string;
  objectives: string[];
  content_blocks: string[];
  status: 'draft' | 'reviewed' | 'approved';
}

interface OutlineData {
  outlines: EmailOutline[];
  metadata: { total: number; levels: string[]; weeks: number };
}

interface EmailSummary {
  language_code: string;
  base_language: string;
  level_week: string;
  level: string;
  week: number;
  subject: string;
  topic: string;
  status: string;
  created_at: string;
  generated_at?: string;
  source_doc_updated_at?: string;
}

interface EmailDetail {
  language_code: string;
  base_language: string;
  level_week: string;
  level: string;
  week: number;
  subject: string;
  topic: string;
  language_name: string;
  body_md: string;
  vocabulary: Array<{ term: string; romanization?: string; gloss: string }>;
  status: string;
  created_at: string;
  updated_at: string;
  generated_at?: string;
  source_doc_updated_at?: string;
}

interface LanguageSummary {
  language_code: string;
  base_language: string;
  count: number;
  latest_generated_at?: string;
}

// ── Constants ──────────────────────────────────────────────────────

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const BASE_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh-TW', label: 'Chinese (Taiwan)' },
  { code: 'es-ES', label: 'Spanish (Castilian)' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' },
  { code: 'fr', label: 'French' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ru', label: 'Russian' },
  { code: 'th', label: 'Thai' },
];

const DEFAULT_COLOR = { bg: 'bg-zinc-900/30', text: 'text-zinc-300', badge: 'bg-zinc-900 text-zinc-300' };

const LEVEL_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  A1: { bg: 'bg-emerald-900/30', text: 'text-emerald-300', badge: 'bg-emerald-900 text-emerald-300' },
  A2: { bg: 'bg-teal-900/30', text: 'text-teal-300', badge: 'bg-teal-900 text-teal-300' },
  B1: { bg: 'bg-blue-900/30', text: 'text-blue-300', badge: 'bg-blue-900 text-blue-300' },
  B2: { bg: 'bg-indigo-900/30', text: 'text-indigo-300', badge: 'bg-indigo-900 text-indigo-300' },
  C1: { bg: 'bg-amber-900/30', text: 'text-amber-300', badge: 'bg-amber-900 text-amber-300' },
  C2: { bg: 'bg-rose-900/30', text: 'text-rose-300', badge: 'bg-rose-900 text-rose-300' },
};

type Tab = 'generated' | 'outlines' | 'generate';

// ── Status badge ───────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-zinc-700 text-zinc-300',
    reviewed: 'bg-amber-900 text-amber-300',
    approved: 'bg-emerald-900 text-emerald-300',
    generated: 'bg-teal-900 text-teal-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-zinc-700 text-zinc-300'}`}>
      {status}
    </span>
  );
}

// ── Generated Emails tab ───────���───────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface DocMeta { key: string; language_code: string; level: string; updated_at?: string }

function GeneratedEmails() {
  const [languages, setLanguages] = useState<LanguageSummary[]>([]);
  const [selectedLang, setSelectedLang] = useState<string>('');
  const [selectedBase, setSelectedBase] = useState<string>('');
  const [emails, setEmails] = useState<EmailSummary[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editTopic, setEditTopic] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editVocab, setEditVocab] = useState<Array<{ term: string; romanization: string; gloss: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [docMetas, setDocMetas] = useState<DocMeta[]>([]);
  const targetLangs = [...new Set(languages.map(l => l.language_code))].sort();
  const baseLangsForTarget = languages
    .filter(l => l.language_code === selectedLang)
    .sort((a, b) => a.base_language.localeCompare(b.base_language));

  const reloadSummary = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient<{ languages: LanguageSummary[] }>('/api/admin/emails'),
      fetch(`${BASE_URL}/api/admin/docs`).then(r => r.ok ? r.json() as Promise<{ docs: DocMeta[] }> : { docs: [] }).catch(() => ({ docs: [] as DocMeta[] })),
    ])
      .then(([emailData, docData]) => {
        setLanguages(emailData.languages || []);
        setDocMetas(docData.docs || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        setLoading(false);
      });
  }, []);

  useEffect(() => { reloadSummary(); }, [reloadSummary]);

  useEffect(() => {
    if (!selectedLang) { setSelectedBase(''); return; }
    const available = languages.filter(l => l.language_code === selectedLang);
    if (available.length === 1) {
      setSelectedBase(available[0]!.base_language);
    } else if (available.some(l => l.base_language === 'en')) {
      setSelectedBase('en');
    } else if (available.length > 0) {
      setSelectedBase(available[0]!.base_language);
    } else {
      setSelectedBase('');
    }
  }, [selectedLang, languages]);

  const loadEmails = useCallback((lang: string, base: string) => {
    setLoadingEmails(true);
    setSelectedEmail(null);
    apiClient<{ emails: EmailSummary[] }>(`/api/admin/emails?language=${lang}&base_language=${base}`)
      .then((d) => {
        setEmails(d.emails || []);
        setLoadingEmails(false);
      })
      .catch(() => {
        setEmails([]);
        setLoadingEmails(false);
      });
  }, []);

  useEffect(() => {
    if (selectedLang && selectedBase) loadEmails(selectedLang, selectedBase);
  }, [selectedLang, selectedBase, loadEmails]);


  const loadDetail = (email: EmailSummary) => {
    const wk = String(email.week).padStart(2, '0');
    const base = email.base_language || 'en';
    const key = `${email.language_code}_${base}_${email.level}_W${wk}`;
    setLoadingDetail(true);
    setEditing(false);
    setSaveMsg(null);
    apiClient<EmailDetail>(`/api/admin/emails/${key}`)
      .then((d) => {
        setSelectedEmail(d);
        setLoadingDetail(false);
      })
      .catch(() => setLoadingDetail(false));
  };

  const startEdit = (e: EmailDetail) => {
    setEditSubject(e.subject);
    setEditTopic(e.topic);
    setEditBody(e.body_md);
    setEditVocab((e.vocabulary || []).map(v => ({ term: v.term, romanization: v.romanization || '', gloss: v.gloss })));
    setEditing(true);
    setSaveMsg(null);
  };

  const saveEmail = async () => {
    if (!selectedEmail) return;
    const e = selectedEmail;
    const wk = String(e.week).padStart(2, '0');
    const base = e.base_language || 'en';
    const key = `${e.language_code}_${base}_${e.level}_W${wk}`;
    setSaving(true);
    setSaveMsg(null);
    try {
      await apiClient(`/api/admin/emails/${key}`, {
        method: 'PUT',
        body: JSON.stringify({
          subject: editSubject,
          topic: editTopic,
          language_name: e.language_name,
          body_md: editBody,
          vocabulary: editVocab,
          status: e.status,
          created_at: e.created_at,
        }),
      });
      const updated: EmailDetail = { ...e, subject: editSubject, topic: editTopic, body_md: editBody, vocabulary: editVocab, updated_at: new Date().toISOString() };
      setSelectedEmail(updated);
      setEditing(false);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err) {
      setSaveMsg(`Error: ${err instanceof Error ? err.message : 'save failed'}`);
    } finally {
      setSaving(false);
    }
  };

  const updateVocabRow = (idx: number, field: 'term' | 'romanization' | 'gloss', value: string) => {
    setEditVocab(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  const addVocabRow = () => {
    setEditVocab(prev => [...prev, { term: '', romanization: '', gloss: '' }]);
  };

  const removeVocabRow = (idx: number) => {
    setEditVocab(prev => prev.filter((_, i) => i !== idx));
  };

  if (loading) {
    return <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading languages...</p>;
  }

  if (error) {
    return <p className="text-red-400 font-mono text-sm py-8 text-center">{error}</p>;
  }

  // ── Detail view ──────────────────────────────────────────────────

  if (selectedEmail) {
    const e = selectedEmail;
    const colors = LEVEL_COLORS[e.level] ?? DEFAULT_COLOR;
    const allForLevel = emails.filter((x) => !filterLevel || x.level === filterLevel);
    const curIdx = allForLevel.findIndex((x) => x.level_week === e.level_week);
    const prev = curIdx > 0 ? allForLevel[curIdx - 1] : null;
    const next = curIdx < allForLevel.length - 1 ? allForLevel[curIdx + 1] : null;
    const docKey = `${e.level}_${e.language_code}`;
    const docMeta = docMetas.find(d => d.key === docKey);
    const isStale = !!(e.source_doc_updated_at && docMeta?.updated_at && docMeta.updated_at > e.source_doc_updated_at);

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={() => { setSelectedEmail(null); setEditing(false); }} className="text-zinc-400 hover:text-zinc-200 text-sm">
              &larr; Back to grid
            </button>
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${colors.badge}`}>{e.level}</span>
            <span className="text-zinc-400 text-sm">Week {e.week}</span>
            <StatusBadge status={e.status} />
            {isStale && (
              <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-900 text-amber-300">STALE</span>
            )}
            {saveMsg && (
              <span className={`text-xs ${saveMsg.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>{saveMsg}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-600 text-xs font-mono">{e.language_code}</span>
            <span className="text-zinc-600 text-xs">/ {BASE_LANGUAGES.find(b => b.code === (e.base_language || 'en'))?.label ?? e.base_language ?? 'en'}</span>
            {!editing ? (
              <button
                onClick={() => startEdit(e)}
                className="px-3 py-1.5 rounded text-xs bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
              >
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 rounded text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEmail}
                  disabled={saving}
                  className="px-3 py-1.5 rounded text-xs bg-teal-700 text-teal-100 hover:bg-teal-600 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
        </div>

        {(e.generated_at || e.source_doc_updated_at) && (
          <div className="bg-zinc-800/50 rounded-lg px-4 py-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
            {e.generated_at && (
              <span className="text-zinc-400">
                Generated: <span className="text-zinc-300 font-mono">{new Date(e.generated_at).toLocaleString()}</span>
                <span className="text-zinc-600 ml-1">({timeAgo(e.generated_at)})</span>
              </span>
            )}
            {e.source_doc_updated_at && (
              <span className="text-zinc-400">
                Source doc: <span className="text-zinc-300 font-mono">{new Date(e.source_doc_updated_at).toLocaleString()}</span>
              </span>
            )}
            {docMeta?.updated_at && (
              <span className={isStale ? 'text-amber-400' : 'text-zinc-400'}>
                Current doc: <span className="font-mono">{new Date(docMeta.updated_at).toLocaleString()}</span>
                {isStale && <span className="ml-1 font-medium"> — CEFR doc updated since last generation</span>}
              </span>
            )}
          </div>
        )}

        {editing ? (
          <>
            <div className="space-y-3">
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Subject</label>
                <input
                  value={editSubject}
                  onChange={(ev) => setEditSubject(ev.target.value)}
                  className="w-full bg-zinc-800 text-zinc-200 rounded border border-zinc-700 px-3 py-2 text-sm focus:border-teal-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Topic</label>
                <input
                  value={editTopic}
                  onChange={(ev) => setEditTopic(ev.target.value)}
                  className="w-full bg-zinc-800 text-zinc-200 rounded border border-zinc-700 px-3 py-2 text-sm focus:border-teal-600 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Body (Markdown)</label>
                <textarea
                  value={editBody}
                  onChange={(ev) => setEditBody(ev.target.value)}
                  className="w-full bg-zinc-800 text-zinc-200 rounded border border-zinc-700 px-3 py-2 text-sm font-mono min-h-[400px] resize-y focus:border-teal-600 focus:outline-none"
                />
              </div>
            </div>

            <div className="bg-zinc-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-zinc-500 text-xs">VOCABULARY</div>
                <button onClick={addVocabRow} className="text-xs text-teal-400 hover:text-teal-300">+ Add row</button>
              </div>
              <div className="space-y-2">
                {editVocab.map((v, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      value={v.term}
                      onChange={(ev) => updateVocabRow(i, 'term', ev.target.value)}
                      placeholder="Term"
                      className="flex-1 bg-zinc-900 text-zinc-200 rounded border border-zinc-700 px-2 py-1 text-sm font-mono focus:border-teal-600 focus:outline-none"
                    />
                    <input
                      value={v.romanization}
                      onChange={(ev) => updateVocabRow(i, 'romanization', ev.target.value)}
                      placeholder="Romanization"
                      className="flex-1 bg-zinc-900 text-zinc-400 rounded border border-zinc-700 px-2 py-1 text-sm italic focus:border-teal-600 focus:outline-none"
                    />
                    <input
                      value={v.gloss}
                      onChange={(ev) => updateVocabRow(i, 'gloss', ev.target.value)}
                      placeholder="English"
                      className="flex-1 bg-zinc-900 text-zinc-400 rounded border border-zinc-700 px-2 py-1 text-sm focus:border-teal-600 focus:outline-none"
                    />
                    <button onClick={() => removeVocabRow(i)} className="text-zinc-600 hover:text-red-400 text-xs px-1">x</button>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold text-zinc-100">{e.subject}</h1>
              <p className="text-zinc-400 mt-1">{e.topic}</p>
            </div>

            {e.body_md && (
              <div className="bg-zinc-800 rounded-lg p-6">
                <div
                  className="max-w-4xl font-body"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(e.body_md) }}
                />
              </div>
            )}

            {e.vocabulary && e.vocabulary.length > 0 && (
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-zinc-500 text-xs mb-2">KEY VOCABULARY</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-zinc-400 border-b border-zinc-700">Term</th>
                        {e.vocabulary.some((v) => v.romanization) && (
                          <th className="px-3 py-2 text-left font-medium text-zinc-400 border-b border-zinc-700">Romanization</th>
                        )}
                        <th className="px-3 py-2 text-left font-medium text-zinc-400 border-b border-zinc-700">English</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e.vocabulary.map((v, i) => (
                        <tr key={i} className="border-b border-zinc-800/50">
                          <td className="px-3 py-2 text-zinc-200 font-mono">{v.term}</td>
                          {e.vocabulary.some((vv) => vv.romanization) && (
                            <td className="px-3 py-2 text-zinc-400 italic">{v.romanization}</td>
                          )}
                          <td className="px-3 py-2 text-zinc-400">{v.gloss}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex items-center gap-3 pt-4 border-t border-zinc-700">
          {prev && (
            <button
              onClick={() => { loadDetail(prev); window.scrollTo(0, 0); }}
              className="px-4 py-2 rounded text-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            >
              &larr; Prev: {prev.level} W{prev.week}
            </button>
          )}
          {next && (
            <button
              onClick={() => { loadDetail(next); window.scrollTo(0, 0); }}
              className="px-4 py-2 rounded text-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            >
              Next: {next.level} W{next.week} &rarr;
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Language selector + grid ─────────────────────────────────────

  if (languages.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500 text-sm">No generated email sequences yet.</p>
        <p className="text-zinc-600 text-xs mt-2">Generate emails from CEFR docs to populate this view.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
          className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm w-full sm:w-auto"
        >
          <option value="">Select language</option>
          {targetLangs.map((lc) => {
            const total = languages.filter(l => l.language_code === lc).reduce((s, l) => s + l.count, 0);
            const bases = languages.filter(l => l.language_code === lc).map(l => l.base_language);
            return (
              <option key={lc} value={lc}>
                {lc} ({total} emails · {bases.length} base{bases.length !== 1 ? 's' : ''})
              </option>
            );
          })}
        </select>

        {selectedLang && baseLangsForTarget.length > 0 && (
          <select
            value={selectedBase}
            onChange={(e) => setSelectedBase(e.target.value)}
            className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
          >
            {baseLangsForTarget.map((l) => (
              <option key={l.base_language} value={l.base_language}>
                {BASE_LANGUAGES.find(b => b.code === l.base_language)?.label ?? l.base_language} ({l.count}{l.latest_generated_at ? ` · ${timeAgo(l.latest_generated_at)}` : ''})
              </option>
            ))}
          </select>
        )}

        {selectedLang && selectedBase && (
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
          >
            <option value="">All levels</option>
            {LEVELS.map((lvl) => (
              <option key={lvl} value={lvl}>{lvl}</option>
            ))}
          </select>
        )}

        {selectedLang && selectedBase && !loadingEmails && (
          <span className="text-zinc-500 text-xs ml-2">
            {emails.length} emails for {selectedLang} / {BASE_LANGUAGES.find(b => b.code === selectedBase)?.label ?? selectedBase}
          </span>
        )}
      </div>

      {selectedLang && selectedBase && !loadingEmails && emails.length > 0 && (() => {
        const levelFreshness = LEVELS.map(lvl => {
          const levelEmails = emails.filter(e => e.level === lvl);
          if (levelEmails.length === 0) return null;
          const docKey = `${lvl}_${selectedLang}`;
          const doc = docMetas.find(d => d.key === docKey);
          const latestGen = levelEmails.reduce((max, e) => e.generated_at && e.generated_at > max ? e.generated_at : max, '');
          const sourceDocAt = levelEmails[0]?.source_doc_updated_at;
          const isStale = !!(sourceDocAt && doc?.updated_at && doc.updated_at > sourceDocAt);
          return { level: lvl, count: levelEmails.length, latestGen, isStale, docUpdated: doc?.updated_at };
        }).filter(Boolean) as { level: string; count: number; latestGen: string; isStale: boolean; docUpdated?: string }[];

        if (levelFreshness.length === 0) return null;
        return (
          <div className="bg-zinc-800/50 rounded-lg px-4 py-3">
            <div className="flex flex-wrap gap-4">
              {levelFreshness.map(lf => {
                const c = LEVEL_COLORS[lf.level] ?? DEFAULT_COLOR;
                return (
                  <div key={lf.level} className="flex items-center gap-2 text-xs">
                    <span className={`font-mono font-bold ${c.text}`}>{lf.level}</span>
                    <span className="text-zinc-500">{lf.count} emails</span>
                    {lf.latestGen && (
                      <span className="text-zinc-500">generated {timeAgo(lf.latestGen)}</span>
                    )}
                    {lf.isStale ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300 font-medium">stale</span>
                    ) : lf.latestGen ? (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-400 font-medium">fresh</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {loadingEmails && (
        <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading emails...</p>
      )}
      {loadingDetail && (
        <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading email...</p>
      )}

      {selectedLang && selectedBase && !loadingEmails && emails.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-zinc-900 z-10 px-3 py-2 text-left text-xs font-medium text-zinc-500 border-b border-zinc-700">
                  Week
                </th>
                {LEVELS.filter((l) => !filterLevel || l === filterLevel).map((lvl) => {
                  const colors = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
                  return (
                    <th key={lvl} className={`px-3 py-2 text-center text-xs font-mono font-bold border-b border-zinc-700 ${colors.text}`}>
                      {lvl}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 25 }, (_, i) => i + 1).map((week) => (
                <tr key={week} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="sticky left-0 bg-zinc-900 z-10 px-3 py-2 text-zinc-400 font-mono text-xs">
                    W{String(week).padStart(2, '0')}
                  </td>
                  {LEVELS.filter((l) => !filterLevel || l === filterLevel).map((lvl) => {
                    const email = emails.find((e) => e.level === lvl && e.week === week);
                    if (!email) {
                      return (
                        <td key={lvl} className="px-2 py-2 text-center text-zinc-700">
                          <span className="text-zinc-700 text-xs">-</span>
                        </td>
                      );
                    }
                    const colors = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
                    return (
                      <td
                        key={lvl}
                        onClick={() => loadDetail(email)}
                        className="px-2 py-2 cursor-pointer hover:bg-zinc-700/30 transition-colors"
                      >
                        <div className={`rounded px-2 py-1 ${colors.bg}`}>
                          <div className={`text-xs truncate max-w-[160px] ${colors.text}`}>
                            {email.subject || email.topic}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                              email.status === 'approved' ? 'bg-emerald-400' :
                              email.status === 'reviewed' ? 'bg-amber-400' :
                              email.status === 'generated' ? 'bg-teal-400' : 'bg-zinc-600'
                            }`} />
                            <span className="text-[10px] text-zinc-500">{email.status}</span>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedLang && selectedBase && !loadingEmails && emails.length === 0 && (
        <p className="text-zinc-500 text-sm py-4 text-center">No emails generated for {selectedLang} / {BASE_LANGUAGES.find(b => b.code === selectedBase)?.label ?? selectedBase} yet.</p>
      )}
    </div>
  );
}

// ── Outlines tab (preserved) ──────────��────────────────────────────

function Outlines() {
  const [data, setData] = useState<OutlineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOutline, setSelectedOutline] = useState<EmailOutline | null>(null);
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [filterWeek, setFilterWeek] = useState<number | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Map<string, 'reviewed' | 'approved'>>(() => {
    try {
      const saved = localStorage.getItem('email-outline-statuses');
      return saved ? new Map(JSON.parse(saved)) : new Map();
    } catch { return new Map(); }
  });
  const [notes, setNotes] = useState<Map<string, string>>(() => {
    try {
      const saved = localStorage.getItem('email-outline-notes');
      return saved ? new Map(JSON.parse(saved)) : new Map();
    } catch { return new Map(); }
  });

  useEffect(() => {
    localStorage.setItem('email-outline-statuses', JSON.stringify([...statusOverrides]));
  }, [statusOverrides]);

  useEffect(() => {
    localStorage.setItem('email-outline-notes', JSON.stringify([...notes]));
  }, [notes]);

  useEffect(() => {
    fetch('/seeds/email-outlines.json')
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
        return res.json() as Promise<OutlineData>;
      })
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { setError(err instanceof Error ? err.message : 'Failed'); setLoading(false); });
  }, []);

  const getStatus = (o: EmailOutline) => statusOverrides.get(`${o.level}_W${o.week}`) ?? o.status;
  const getNotes = (o: EmailOutline) => notes.get(`${o.level}_W${o.week}`) ?? '';

  if (loading) return <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading outlines...</p>;
  if (error || !data) return <p className="text-red-400 font-mono text-sm py-8 text-center">{error ?? 'No data'}</p>;

  const filteredOutlines = data.outlines.filter((o) => {
    if (filterLevel && o.level !== filterLevel) return false;
    if (filterWeek !== null && o.week !== filterWeek) return false;
    return true;
  });

  const reviewedCount = data.outlines.filter((o) => { const s = getStatus(o); return s === 'reviewed' || s === 'approved'; }).length;
  const approvedCount = data.outlines.filter((o) => getStatus(o) === 'approved').length;

  if (selectedOutline) {
    const o = selectedOutline;
    const colors = LEVEL_COLORS[o.level] ?? DEFAULT_COLOR;
    const st = getStatus(o);
    const curIdx = filteredOutlines.findIndex((x) => x.level === o.level && x.week === o.week);
    const prev = curIdx > 0 ? filteredOutlines[curIdx - 1] : null;
    const next = curIdx < filteredOutlines.length - 1 ? filteredOutlines[curIdx + 1] : null;

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => setSelectedOutline(null)} className="text-zinc-400 hover:text-zinc-200 text-sm">&larr; Back</button>
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${colors.badge}`}>{o.level}</span>
          <span className="text-zinc-400 text-sm">Week {o.week}</span>
          <StatusBadge status={st} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">{o.subject}</h1>
          <p className="text-zinc-400 mt-1">{o.topic} &middot; {o.cefr_source}</p>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs mb-2">Learning Objectives</div>
          <ul className="space-y-1">
            {o.objectives.map((obj, i) => (
              <li key={i} className="text-zinc-300 text-sm flex gap-2"><span className="text-teal-500">-</span>{obj}</li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs mb-2">Content Blocks</div>
          <div className="space-y-2">
            {o.content_blocks.map((b, i) => (
              <div key={i} className="text-zinc-300 text-sm bg-zinc-700/50 rounded px-3 py-2">{b}</div>
            ))}
          </div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs mb-2">Notes</div>
          <textarea
            value={getNotes(o)}
            onChange={(e) => {
              const key = `${o.level}_W${o.week}`;
              setNotes((prev) => new Map(prev).set(key, e.target.value));
            }}
            placeholder="Leave feedback..."
            className="w-full bg-zinc-900 text-zinc-200 text-sm rounded border border-zinc-700 px-3 py-2 min-h-[80px] resize-y focus:border-teal-600 focus:outline-none"
          />
        </div>
        <div className="flex gap-3">
          {st !== 'reviewed' && st !== 'approved' && (
            <button
              onClick={() => setStatusOverrides((p) => new Map(p).set(`${o.level}_W${o.week}`, 'reviewed'))}
              className="px-4 py-2 rounded bg-amber-800 text-amber-200 hover:bg-amber-700 text-sm"
            >Mark Reviewed</button>
          )}
          {st !== 'approved' && (
            <button
              onClick={() => setStatusOverrides((p) => new Map(p).set(`${o.level}_W${o.week}`, 'approved'))}
              className="px-4 py-2 rounded bg-emerald-800 text-emerald-200 hover:bg-emerald-700 text-sm"
            >Approve</button>
          )}
        </div>
        <div className="flex gap-3 pt-4 border-t border-zinc-700">
          {prev && <button onClick={() => { setSelectedOutline(prev); window.scrollTo(0, 0); }} className="px-4 py-2 rounded text-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700">&larr; {prev.level} W{prev.week}</button>}
          {next && <button onClick={() => { setSelectedOutline(next); window.scrollTo(0, 0); }} className="px-4 py-2 rounded text-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700">{next.level} W{next.week} &rarr;</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-zinc-400 text-sm">
        {data.metadata.total} outlines &middot; {reviewedCount} reviewed &middot; {approvedCount} approved
      </p>
      <div className="flex flex-wrap gap-3 items-center">
        <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}
          className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm">
          <option value="">All levels</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={filterWeek ?? ''} onChange={(e) => setFilterWeek(e.target.value ? parseInt(e.target.value, 10) : null)}
          className="bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm">
          <option value="">All weeks</option>
          {Array.from({ length: 25 }, (_, i) => i + 1).map((w) => <option key={w} value={w}>Week {w}</option>)}
        </select>
        <span className="text-zinc-500 text-xs ml-2">Showing {filteredOutlines.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-zinc-900 z-10 px-3 py-2 text-left text-xs font-medium text-zinc-500 border-b border-zinc-700">Week</th>
              {LEVELS.map((lvl) => {
                const c = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
                return <th key={lvl} className={`px-3 py-2 text-center text-xs font-mono font-bold border-b border-zinc-700 ${c.text}`}>{lvl}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 25 }, (_, i) => i + 1).map((week) => (
              <tr key={week} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                <td className="sticky left-0 bg-zinc-900 z-10 px-3 py-2 text-zinc-400 font-mono text-xs">W{String(week).padStart(2, '0')}</td>
                {LEVELS.map((lvl) => {
                  const outline = data.outlines.find((o) => o.week === week && o.level === lvl);
                  if (!outline) return <td key={lvl} className="px-2 py-2 text-center text-zinc-700">-</td>;
                  const st = getStatus(outline);
                  const colors = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
                  return (
                    <td key={lvl} onClick={() => setSelectedOutline(outline)} className="px-2 py-2 cursor-pointer hover:bg-zinc-700/30 transition-colors">
                      <div className={`rounded px-2 py-1 ${colors.bg}`}>
                        <div className={`text-xs truncate max-w-[140px] ${colors.text}`}>{outline.topic}</div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${st === 'approved' ? 'bg-emerald-400' : st === 'reviewed' ? 'bg-amber-400' : 'bg-zinc-600'}`} />
                          <span className="text-[10px] text-zinc-500">{st}</span>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────���───────────────────────────

// ── CEFR doc fetcher (shared between seeds and DB) ────────────────

const TRANSCRIPTION_HEADERS: Record<string, string> = {
  RUS: 'ISO 9 transcription used (я = â)',
  UKR: 'BGN/PCGN transcription used (и = y)',
  BUL: 'Streamlined ISO 9 transcription used (ш = š)',
  SRP: 'Serbian Latin transcription used (ш = š)',
  MKD: 'Metagraph Macedonian transcription used (ш = š)',
  BEL: 'ISO 9 transcription used (я = â)',
  RUE: 'Metagraph Rusyn transcription used (я = â)',
  TAT: 'Metagraph Tatar transcription used (ш = ş)',
  KAZ: 'Official Kazakh Latin (2021) used',
  KOR: 'Revised Romanization used (한국어 = hangugeo)',
  JPN: 'Modified Hepburn romanization used (日本語 = nihongo)',
  CMN_CN: 'Hanyu Pinyin romanization used (中文 = zhōngwén)',
  CMN_TW: 'Hanyu Pinyin romanization used (中文 = zhōngwén)',
  THA: 'Paiboon romanization used (ภาษาไทย = paasǎa tai)',
  ARB: 'DIN 31635 romanization used (العربية = al-ʿarabiyyah)',
  ARZ: 'Glossika Egyptian romanization used (المصرية = el-maṣreyya)',
  APC: 'Glossika Levantine romanization used (العربية = el-ʿarabiyye)',
  AFB: 'Glossika Gulf romanization used (العربية = el-ʿarabiyya)',
  PES: 'UN romanization used (فارسی = fārsi)',
  HIN: 'IAST romanization used (हिन्दी = hindī)',
  BEN: 'IAST romanization used (বাংলা = bāṁlā)',
  TAM: 'ISO 15919 romanization used (தமிழ் = tamiḻ)',
  KHM: 'Phonemic romanization used (ភាសាខ្មែរ = phiəsaa khmae)',
  KHK: 'MNS transliteration used (монгол = mongol)',
  MYA: 'MLC transcription used (မြန်မာ = mranmā)',
  ELL: 'ELOT 743 romanization used (ελληνικά = elliniká)',
  HYE: 'Hübschmann-Meillet romanization used (հայերեն = hayeren)',
  KAT: 'ISO 9984 romanization used (ქართული = kartuli)',
  HEB: 'Metagraph Hebrew romanization used (עברית = ʿivrit)',
  NAN_TW: 'Tâi-lô romanization used',
  HAK_SX: 'Hagfa Pinyim romanization used',
  YUE_HK: 'Jyutping romanization used (粵語 = jyut6jyu5)',
  CKB: 'Kurdish Latin (Bedirxan) transcription used',
  UIG: 'Metagraph Uyghur romanization used',
};

async function fetchCefrDoc(key: string): Promise<string | null> {
  try {
    const res = await fetch(`${BASE_URL}/api/admin/docs/${encodeURIComponent(key)}`);
    if (res.ok) {
      const data = await res.json() as { content?: string };
      if (data.content) return data.content;
    }
  } catch { /* fall through */ }
  return null;
}

async function fetchCefrDocWithMeta(key: string): Promise<{ content: string; updated_at?: string } | null> {
  try {
    const res = await fetch(`${BASE_URL}/api/admin/docs/${encodeURIComponent(key)}`);
    if (res.ok) {
      const data = await res.json() as { content?: string; updated_at?: string };
      if (data.content) return { content: data.content, updated_at: data.updated_at };
    }
  } catch { /* fall through */ }
  return null;
}

// ── Generate tab ──────────────────────────────────────────────────

interface CefrDocEntry {
  key: string;
  language_code: string;
  level: string;
  status?: string;
  updated_at?: string;
}

function GenerateEmails() {
  const [dbDocs, setDbDocs] = useState<CefrDocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [baseLang, setBaseLang] = useState('en');
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [showFixReport, setShowFixReport] = useState(false);
  const [copied, setCopied] = useState(false);
  const [outlines, setOutlines] = useState<EmailOutline[]>([]);
  const [generating, setGenerating] = useState(false);
  const [genLogs, setGenLogs] = useState<GenerationLog[]>([]);
  const [genResult, setGenResult] = useState<{ success: boolean; count: number } | null>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE_URL}/api/admin/docs`)
        .then(r => r.ok ? r.json() as Promise<{ docs: CefrDocEntry[] }> : { docs: [] })
        .catch(() => ({ docs: [] as CefrDocEntry[] })),
      fetch('/seeds/email-outlines.json')
        .then(r => r.ok ? r.json() as Promise<OutlineData> : null)
        .catch(() => null),
    ]).then(([{ docs }, outlinesData]) => {
      setDbDocs(docs);
      if (outlinesData) setOutlines(outlinesData.outlines);
      setLoading(false);
    });
  }, []);

  const availableLangs = new Set<string>();
  for (const d of dbDocs) availableLangs.add(d.language_code);
  const langList = [...availableLangs].sort((a, b) =>
    langName(a).localeCompare(langName(b)));

  const findDoc = (lvl: string, lang: string): CefrDocEntry | undefined => {
    const suffix = baseLang !== 'en' ? `__${baseLang}` : '';
    const key = `${lvl}_${lang}${suffix}`;
    const doc = dbDocs.find(d => d.key === key);
    if (doc) return doc;
    if (suffix) return dbDocs.find(d => d.key === `${lvl}_${lang}`);
    return undefined;
  };

  const hasDoc = (lvl: string, lang: string): boolean => !!findDoc(lvl, lang);

  const docStatusLabel = (lvl: string, lang: string): string => {
    const doc = findDoc(lvl, lang);
    if (!doc) return ' (no doc)';
    const s = doc.status ?? 'draft';
    if (s === 'approved') return '';
    return ` (${s})`;
  };

  const runValidation = async () => {
    if (!selectedLang) return;
    setValidating(true);
    setResult(null);
    setShowFixReport(false);

    const blSuffix = baseLang !== 'en' ? `__${baseLang}` : '';
    const targetKey = `${selectedLevel}_${selectedLang}${blSuffix}`;
    const a0Key = `A0_${selectedLang}${blSuffix}`;

    const [cefrMd, a0Md] = await Promise.all([
      fetchCefrDoc(targetKey),
      (selectedLevel === 'A1' || selectedLevel === 'A2') ? fetchCefrDoc(a0Key) : Promise.resolve(null),
    ]);

    if (!cefrMd) {
      const blLabel = baseLang !== 'en' ? ` (${BASE_LANGUAGES.find(b => b.code === baseLang)?.label ?? baseLang} base)` : '';
      setResult({
        level: selectedLevel,
        languageCode: selectedLang,
        a0SectionCount: 0,
        a0SpilloverToA2: 0,
        checks: [{
          id: 'doc_exists',
          label: `${selectedLevel} document exists${blLabel}`,
          pass: false,
          detail: `No ${selectedLevel} document found for ${selectedLang}${blLabel}. Generate the CEFR document first.`,
        }],
        allPassed: false,
        fixReport: `No ${targetKey} document found. Create the CEFR document${blLabel} before generating emails.`,
      });
      setValidating(false);
      return;
    }

    await patternsReady;
    const validation = validateCefrDoc(selectedLevel, selectedLang, cefrMd, a0Md, baseLang);
    setResult(validation);
    setValidating(false);
  };

  const copyFixReport = () => {
    if (!result?.fixReport) return;
    navigator.clipboard.writeText(result.fixReport);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerate = async (override = false) => {
    if (!selectedLang || (!override && !result?.allPassed)) return;
    setGenerating(true);
    setGenLogs([]);
    setGenResult(null);

    const selectedLangName = langName(selectedLang);
    const header = TRANSCRIPTION_HEADERS[selectedLang] ?? null;

    const blSuffix = baseLang !== 'en' ? `__${baseLang}` : '';
    const targetKey = `${selectedLevel}_${selectedLang}${blSuffix}`;
    const a0Key = `A0_${selectedLang}${blSuffix}`;

    const [cefrResult, a0Md] = await Promise.all([
      fetchCefrDocWithMeta(targetKey),
      (selectedLevel === 'A1' || selectedLevel === 'A2') ? fetchCefrDoc(a0Key) : Promise.resolve(null),
    ]);

    if (!cefrResult) {
      setGenLogs([{ timestamp: new Date().toISOString(), message: 'Failed to load CEFR document', level: 'error' }]);
      setGenResult({ success: false, count: 0 });
      setGenerating(false);
      return;
    }

    const genOutput = generateEmails(
      selectedLang, selectedLangName, selectedLevel, cefrResult.content, a0Md,
      outlines, header,
      (log) => setGenLogs(prev => [...prev, log]),
      cefrResult.updated_at,
      baseLang,
    );

    if (!genOutput.success) {
      setGenResult({ success: false, count: 0 });
      setGenerating(false);
      return;
    }

    // Delete old emails for this language+base+level before uploading to prevent doubling
    try {
      const delUrl = `${BASE_URL}/api/admin/emails?language=${selectedLang}&base_language=${baseLang}&level=${selectedLevel}`;
      const delRes = await fetch(delUrl, { method: 'DELETE' });
      if (delRes.ok) {
        const delData = await delRes.json() as { deleted: number };
        setGenLogs(prev => [...prev, { timestamp: new Date().toISOString(), message: `Deleted ${delData.deleted} old emails before upload`, level: 'info' }]);
      } else {
        setGenLogs(prev => [...prev, { timestamp: new Date().toISOString(), message: `Warning: failed to delete old emails (${delRes.status})`, level: 'warn' }]);
      }
    } catch (err) {
      setGenLogs(prev => [...prev, { timestamp: new Date().toISOString(), message: `Warning: delete old emails failed: ${err instanceof Error ? err.message : String(err)}`, level: 'warn' }]);
    }

    const uploadResult = await uploadEmails(
      genOutput.emails, BASE_URL,
      (log) => setGenLogs(prev => [...prev, log]),
    );

    setGenResult({ success: uploadResult.success, count: genOutput.emails.length });
    setGenerating(false);
  };

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [genLogs]);

  if (loading) {
    return <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading available documents...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 space-y-4">
        <div className="flex gap-4 items-end flex-wrap">
          <div>
            <label className="text-zinc-500 text-xs block mb-1">Base language</label>
            <select
              value={baseLang}
              onChange={e => setBaseLang(e.target.value)}
              className="bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
            >
              {BASE_LANGUAGES.map(bl => (
                <option key={bl.code} value={bl.code}>{bl.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-zinc-500 text-xs block mb-1">Target language</label>
            <select
              value={selectedLang}
              onChange={e => setSelectedLang(e.target.value)}
              className="bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm min-w-[200px]"
            >
              <option value="">Select language...</option>
              {langList.map(code => (
                <option key={code} value={code}>{langName(code)} ({code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-zinc-500 text-xs block mb-1">CEFR level</label>
            <select
              value={selectedLevel}
              onChange={e => setSelectedLevel(e.target.value)}
              className="bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
            >
              {LEVELS.map(lvl => (
                <option key={lvl} value={lvl} disabled={!selectedLang || !hasDoc(lvl, selectedLang)}>
                  {lvl}{selectedLang ? docStatusLabel(lvl, selectedLang) : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={runValidation}
            disabled={!selectedLang || validating}
            className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700 disabled:opacity-50"
          >
            {validating ? 'Validating...' : 'Run Pre-flight Check'}
          </button>
        </div>

        {selectedLang && (
          <div className="space-y-2">
            <div className="flex gap-3 text-xs text-zinc-500 flex-wrap">
              <span>A0: {hasDoc('A0', selectedLang) ? (findDoc('A0', selectedLang)?.status === 'approved' ? '✓' : findDoc('A0', selectedLang)?.status ?? 'draft') : 'missing'}</span>
              {LEVELS.map(lvl => {
                const doc = findDoc(lvl, selectedLang);
                const status = doc?.status ?? 'draft';
                const color = !doc ? 'text-zinc-700' : status === 'approved' ? 'text-emerald-400' : 'text-amber-400';
                return (
                  <span key={lvl} className={color}>
                    {lvl}: {!doc ? 'missing' : status === 'approved' ? '✓' : status}
                  </span>
                );
              })}
            </div>
            {TRANSCRIPTION_HEADERS[selectedLang] && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-zinc-500">Romanization:</span>
                <code className="text-teal-400 font-mono bg-zinc-900 px-2 py-0.5 rounded">{TRANSCRIPTION_HEADERS[selectedLang]}</code>
              </div>
            )}
            {!TRANSCRIPTION_HEADERS[selectedLang] && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-zinc-600">No romanization header (Latin-script language)</span>
              </div>
            )}
          </div>
        )}
      </div>

      {result && (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-zinc-100 font-medium">
                Pre-flight Check: {langName(result.languageCode)} {result.level}
              </h3>
              {result.allPassed ? (
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-900 text-emerald-300">ALL PASSED</span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-900 text-red-300">
                  {result.checks.filter(c => !c.pass).length} FAILED
                </span>
              )}
            </div>
            <button onClick={() => setResult(null)} className="text-zinc-500 hover:text-zinc-300 text-sm">
              Dismiss
            </button>
          </div>

          <div className="p-4 space-y-3">
            {(result.level === 'A1' || result.level === 'A2') && (
              <div className="bg-zinc-900 rounded px-3 py-2 flex items-center gap-4 text-sm">
                <span className="text-zinc-400">A0 phonics sections:</span>
                <span className="font-mono text-zinc-200">{result.a0SectionCount}</span>
                {result.level === 'A1' && result.a0SectionCount > 25 && (
                  <span className="text-amber-400 text-xs">
                    ({result.a0SpilloverToA2} sections spill to A2)
                  </span>
                )}
                {result.level === 'A1' && result.a0SectionCount > 0 && result.a0SectionCount <= 25 && (
                  <span className="text-zinc-500 text-xs">
                    ({result.a0SectionCount} of 25 email slots used)
                  </span>
                )}
                {result.level === 'A2' && result.a0SpilloverToA2 > 0 && (
                  <span className="text-teal-400 text-xs">
                    ({result.a0SpilloverToA2} A0 spillover sections to include in A2 emails)
                  </span>
                )}
                {result.a0SectionCount === 0 && (
                  <span className="text-zinc-600 text-xs">No A0 document found</span>
                )}
              </div>
            )}

            <div className="space-y-1">
              {result.checks.map(check => (
                <CheckRow key={check.id} check={check} />
              ))}
            </div>

            {TRANSCRIPTION_HEADERS[result.languageCode] && (
              <div className="bg-zinc-900 rounded px-3 py-2 flex items-center gap-3 text-sm">
                <span className="text-zinc-400">Email header:</span>
                <code className="text-teal-300 font-mono text-xs">{TRANSCRIPTION_HEADERS[result.languageCode]}</code>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              {result.allPassed && (
                <button
                  onClick={() => handleGenerate()}
                  disabled={generating}
                  className="px-4 py-2 rounded text-sm font-medium bg-emerald-800 text-emerald-200 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? 'Generating...' : `Generate ${result.level} Emails`}
                </button>
              )}
              {!result.allPassed && (
                <>
                  <button
                    onClick={() => handleGenerate(true)}
                    disabled={generating}
                    className="px-4 py-2 rounded text-sm font-medium bg-red-900 text-red-200 hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed border border-red-700"
                  >
                    {generating ? 'Generating...' : `Generate Anyway (${result.checks.filter(c => !c.pass).length} issues)`}
                  </button>
                  <button
                    onClick={() => setShowFixReport(!showFixReport)}
                    className="px-4 py-2 rounded text-sm font-medium bg-amber-800 text-amber-200 hover:bg-amber-700"
                  >
                    {showFixReport ? 'Hide Fix Report' : 'View Fix Report'}
                  </button>
                  <button
                    onClick={copyFixReport}
                    className="px-4 py-2 rounded text-sm font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
                  >
                    {copied ? 'Copied!' : 'Copy Fix Report'}
                  </button>
                </>
              )}
            </div>

            {genResult && (
              <div className={`rounded px-3 py-2 text-sm font-medium ${genResult.success ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700' : 'bg-red-900/50 text-red-300 border border-red-700'}`}>
                {genResult.success
                  ? `${genResult.count} emails generated and uploaded successfully`
                  : 'Generation failed — check logs below'}
              </div>
            )}

            {genLogs.length > 0 && (
              <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-4 mt-2 overflow-y-auto max-h-[400px]">
                <h4 className="text-zinc-500 text-xs font-medium mb-2">GENERATION LOG</h4>
                <div className="space-y-0.5 font-mono text-xs">
                  {genLogs.map((l, i) => (
                    <div key={i} className={l.level === 'error' ? 'text-red-400' : l.level === 'warn' ? 'text-amber-400' : 'text-zinc-400'}>
                      <span className="text-zinc-600">{l.timestamp.slice(11, 19)}</span>{' '}
                      {l.message}
                    </div>
                  ))}
                  <div ref={logEndRef} />
                </div>
              </div>
            )}

            {showFixReport && result.fixReport && (
              <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-4 mt-2 overflow-y-auto max-h-[500px]">
                <div className="font-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(result.fixReport) }} />
              </div>
            )}
          </div>
        </div>
      )}

      {!result && selectedLang && (
        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4">
          <h3 className="text-zinc-400 text-xs font-medium mb-2">EMAIL STRUCTURE PER LEVEL</h3>
          <div className="grid grid-cols-2 gap-4 text-xs text-zinc-500">
            <div>
              <p className="text-zinc-300 font-medium mb-1">A1 (25 emails)</p>
              <ul className="space-y-0.5">
                <li>Sections 1-7 content spread evenly</li>
                <li>Section 8 split: Email 13 + Email 25</li>
                <li>Section 9: one subsection per email</li>
                <li>Section 10: grammar samples (at least 2/email)</li>
                <li>A0 phonics: one section per email</li>
              </ul>
            </div>
            <div>
              <p className="text-zinc-300 font-medium mb-1">A2 (25 emails)</p>
              <ul className="space-y-0.5">
                <li>Same structure as A1</li>
                <li>A0 spillover phonics (if &gt;25 sections)</li>
                <li>No new A0 content if all fit in A1</li>
              </ul>
              <p className="text-zinc-300 font-medium mt-2 mb-1">B1-C2 (25 emails each)</p>
              <ul className="space-y-0.5">
                <li>Same structure, no A0 phonics</li>
                <li>B1: [GLO] only for non-Latin scripts</li>
                <li>B2-C2: no [GLO] requirement</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckRow({ check }: { check: CheckResult }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`rounded px-3 py-2 cursor-pointer transition-colors ${
        check.pass ? 'bg-emerald-900/20 hover:bg-emerald-900/30' : 'bg-red-900/20 hover:bg-red-900/30'
      }`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-3">
        <span className={`text-lg ${check.pass ? 'text-emerald-400' : 'text-red-400'}`}>
          {check.pass ? '✓' : '✗'}
        </span>
        <span className={`text-sm flex-1 ${check.pass ? 'text-emerald-300' : 'text-red-300'}`}>
          {check.label}
        </span>
        <span className="text-zinc-600 text-xs">{expanded ? '▼' : '▶'}</span>
      </div>
      {expanded && (
        <p className="text-xs text-zinc-400 mt-1 ml-8">{check.detail}</p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────

export default function Emails() {
  const [tab, setTab] = useState<Tab>('generated');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-100">Email Sequences</h1>
        <div className="flex gap-1 bg-zinc-800 rounded p-0.5">
          <button
            onClick={() => setTab('generated')}
            className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${
              tab === 'generated' ? 'bg-teal-800 text-teal-200' : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            Generated
          </button>
          <button
            onClick={() => setTab('outlines')}
            className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${
              tab === 'outlines' ? 'bg-teal-800 text-teal-200' : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            Outlines
          </button>
          <button
            onClick={() => setTab('generate')}
            className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${
              tab === 'generate' ? 'bg-teal-800 text-teal-200' : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            Generate
          </button>
        </div>
      </div>

      {tab === 'generated' ? <GeneratedEmails /> : tab === 'outlines' ? <Outlines /> : <GenerateEmails />}
    </div>
  );
}
